import React from "react";

/**
 * NumericInput
 * Props:
 * - value (string|number)
 * - onChange (newStringValue) => void
 * - allowDecimal (default true)
 * - allowNegative (default false)
 * - maxDigits (counts only 0-9 digits)
 * - maxLength (character length)
 * - inputMode (optional)
 * - ...rest (other input props)
 */
export default function NumericInput({
  value,
  onChange,
  allowDecimal = true,
  allowNegative = false,
  maxDigits,
  maxLength,
  inputMode,
  ...rest
}) {
  const resolvedInputMode = inputMode || (allowDecimal ? "decimal" : "numeric");

  const sanitize = (rawValue) => {
    if (rawValue === "" || rawValue === null || rawValue === undefined) return "";

    let v = String(rawValue);

    // allow intermediate typing states
    if (v === "-" && allowNegative) return "-";
    if ((v === "." || v === "-.") && allowDecimal) return v;

    // remove invalid chars (keep digits, dot, minus)
    v = v.replace(/[^0-9.\-]/g, "");

    // minus only at start (and only if allowed)
    if (!allowNegative) v = v.replace(/-/g, "");
    else {
      // keep single leading minus
      v = v.replace(/-/g, (m, i) => (i === 0 ? "-" : ""));
    }

    // only one dot if decimals allowed
    if (allowDecimal) {
      const firstDot = v.indexOf(".");
      if (firstDot !== -1) {
        v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, "");
      }
    } else {
      v = v.replace(/\./g, "");
    }

    // enforce maxDigits (digits only)
    if (typeof maxDigits === "number") {
      const sign = v.startsWith("-") ? "-" : "";
      const core = v.startsWith("-") ? v.slice(1) : v;
      const digitsOnly = core.replace(/\./g, "");
      if (digitsOnly.length > maxDigits) {
        // trim digits to maxDigits from left (int+dec handling kept simple)
        const allowedDigits = digitsOnly.slice(0, maxDigits);
        if (core.includes(".")) {
          // rebuild: split int/dec, then reassign trimmed digits preserving decimal split if possible
          const parts = core.split(".");
          const intPartLen = Math.max(0, Math.min(parts[0].length, allowedDigits.length - (parts[1] ? parts[1].length : 0)));
          const intPart = allowedDigits.slice(0, intPartLen) || "0";
          const decPart = allowedDigits.slice(intPartLen);
          v = intPart + (decPart ? "." + decPart : "");
        } else {
          v = allowedDigits;
        }
        if (sign) v = sign + v;
      }
    }

    // enforce maxLength (character-level)
    if (typeof maxLength === "number" && v.length > maxLength) {
      v = v.slice(0, maxLength);
    }

    return v;
  };

  const handleChange = (e) => {
    const next = sanitize(e.target.value);
    if (next !== null) onChange(next);
  };

  const handlePaste = (e) => {
    const text = e.clipboardData.getData("text");
    const next = sanitize(text);
    if (next === null || next === "") {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    onChange(next);
  };

  const handleBlur = (e) => {
    const v = e.target.value;
    if (v === "-" || v === "." || v === "-.") {
      onChange("");
      return;
    }
    // remove trailing dot on blur: "12." -> "12"
    if (v.endsWith(".")) {
      onChange(v.slice(0, -1));
      return;
    }
  };

  return (
    <input
      {...rest}
      type="text"
      inputMode={resolvedInputMode}
      value={value ?? ""}
      onChange={handleChange}
      onPaste={handlePaste}
      onBlur={handleBlur}
      maxLength={maxLength}
    />
  );
}
